import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import PageHeader from "@/components/common/PageHeader";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import { toast } from "sonner";
import * as Icons from "lucide-react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import api from "@/lib/api";

const ICON_OPTIONS = [
  { value: "fa-brands fa-linkedin", label: "LinkedIn" },
  { value: "fa-brands fa-github", label: "GitHub" },
  { value: "fa-brands fa-twitter", label: "Twitter" },
  { value: "fa-brands fa-instagram", label: "Instagram" },
  { value: "fa-brands fa-facebook", label: "Facebook" },
  { value: "fa-brands fa-youtube", label: "YouTube" },
  { value: "fa-brands fa-dribbble", label: "Dribbble" },
  { value: "fa-brands fa-behance", label: "Behance" },
  { value: "fa-solid fa-envelope", label: "Email" },
  { value: "fa-solid fa-link", label: "Link" },
  { value: "fa-solid fa-code", label: "Code" },
  { value: "fa-solid fa-laptop-code", label: "Laptop Code" },
  { value: "fa-solid fa-database", label: "Database" },
  { value: "fa-solid fa-server", label: "Server" },
  { value: "fa-brands fa-react", label: "React" },
  { value: "fa-brands fa-node-js", label: "Node.js" },
  { value: "fa-brands fa-python", label: "Python" },
  { value: "fa-brands fa-js", label: "JavaScript" },
  { value: "fa-brands fa-html5", label: "HTML5" },
  { value: "fa-brands fa-css3-alt", label: "CSS3" },
  { value: "fa-solid fa-pen-nib", label: "Design" },
  { value: "fa-solid fa-camera", label: "Photography" },
  { value: "fa-solid fa-gamepad", label: "Gaming" },
  { value: "fa-solid fa-music", label: "Music" },
  { value: "fa-solid fa-plane", label: "Travel" },
  { value: "fa-solid fa-book", label: "Reading" },
  { value: "fa-solid fa-dumbbell", label: "Fitness" },
  { value: "fa-solid fa-heart", label: "Heart" },
  { value: "fa-solid fa-star", label: "Star" },
  { value: "fa-solid fa-briefcase", label: "Briefcase" },
  { value: "fa-solid fa-chart-line", label: "Chart Line" },
  { value: "fa-solid fa-chart-pie", label: "Chart Pie" },
  { value: "fa-solid fa-users", label: "Users" },
  { value: "fa-solid fa-user-tie", label: "User Tie" },
  { value: "fa-solid fa-headset", label: "Support" },
  { value: "fa-solid fa-sack-dollar", label: "Money" },
  { value: "fa-solid fa-handshake", label: "Handshake" },
  { value: "fa-solid fa-bullhorn", label: "Marketing" },
  { value: "fa-solid fa-person-running", label: "Running" },
  { value: "fa-solid fa-person-swimming", label: "Swimming" },
  { value: "fa-solid fa-person-biking", label: "Biking" },
  { value: "fa-solid fa-mountain", label: "Hiking/Outdoors" },
  { value: "fa-solid fa-campground", label: "Camping" },
  { value: "fa-solid fa-futbol", label: "Soccer" },
  { value: "fa-solid fa-basketball", label: "Basketball" },
  { value: "fa-solid fa-medal", label: "Medal" },
  { value: "fa-solid fa-microphone", label: "Public Speaking" },
  { value: "fa-solid fa-chalkboard-user", label: "Teaching" },
  { value: "fa-solid fa-people-group", label: "Audience" },
  { value: "fa-solid fa-comments", label: "Discussion" },
  { value: "fa-solid fa-podcast", label: "Podcast" },
  { value: "fa-solid fa-pen-fancy", label: "Writing" }
];

const blank = { interestName: "", iconUrl: "Heart", description: "", displayOrder: 1 };

export default function Interests() {
  const [items, setItems] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchInterests = async () => {
    try {
      setLoading(true);
      const res = await api.get("/Interest");
      if (res.success) {
        setItems(res.data || []);
      } else {
        toast.error(res.message || "Failed to fetch interests");
      }
    } catch (err) {
      toast.error(err?.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInterests();
  }, []);

  const save = async () => {
    if (!form.interestName) {
      toast.error("Name is required.");
      return;
    }
    
    try {
      if (editing) {
        const res = await api.put(`/Interest/${editing.id}`, form);
        if (res.success) {
          toast.success("Interest updated.");
          fetchInterests();
          setModalOpen(false);
        } else {
          toast.error(res.message || "Update failed");
        }
      } else {
        const res = await api.post("/Interest", form);
        if (res.success) {
          toast.success("Interest added.");
          fetchInterests();
          setModalOpen(false);
        } else {
          toast.error(res.message || "Creation failed");
        }
      }
    } catch (err) {
      toast.error(err?.message || "An error occurred");
    }
  };

  const handleDelete = async () => {
    try {
      const res = await api.delete(`/Interest/${toDelete.id}`);
      if (res.success) {
        toast.success("Interest removed.");
        fetchInterests();
      } else {
        toast.error(res.message || "Failed to delete");
      }
    } catch (err) {
      toast.error(err?.message || "An error occurred");
    } finally {
      setConfirmOpen(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Interests"
        description="The things that fuel you outside the keyboard."
        testId="interests"
        action={() => { setEditing(null); setForm(blank); setModalOpen(true); }}
        actionLabel="Add Interest"
        actionIcon={Plus}
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {items.map((i) => {
          const isUrl = i.iconUrl && (i.iconUrl.startsWith("http") || i.iconUrl.startsWith("/"));
          const isFA = i.iconUrl && (i.iconUrl.includes("fa-") || i.iconUrl.includes("fa "));
          const Icon = (!isUrl && !isFA) ? (Icons[i.iconUrl] || Icons.Heart) : null;
          
          return (
            <Card
              key={i.id}
              data-testid={`interest-card-${i.id}`}
              className="group p-5 hover:border-foreground/20 hover:-translate-y-0.5 transition-all duration-200"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center group-hover:scale-110 transition-transform overflow-hidden">
                  {isUrl ? (
                    <img src={i.iconUrl} alt="icon" className="h-6 w-6 object-contain" />
                  ) : isFA ? (
                    <i className={`${i.iconUrl} text-xl`}></i>
                  ) : (
                    <Icon className="h-5 w-5" strokeWidth={1.5} />
                  )}
                </div>
                <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => { setEditing(i); setForm(i); setModalOpen(true); }} data-testid={`interest-edit-${i.id}`}>
                    <Pencil className="h-3 w-3" strokeWidth={1.5} />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7 text-destructive"
                    onClick={() => { setToDelete(i); setConfirmOpen(true); }}
                    data-testid={`interest-delete-${i.id}`}
                  >
                    <Trash2 className="h-3 w-3" strokeWidth={1.5} />
                  </Button>
                </div>
              </div>
              <h3 className="font-display text-base font-semibold tracking-tight">{i.interestName}</h3>
              <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{i.description}</p>
            </Card>
          );
        })}
      </div>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent data-testid="interest-modal">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Edit interest" : "Add interest"}</DialogTitle>
            <DialogDescription>What fuels you outside the keyboard.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Name</Label>
                <Input data-testid="interest-name" value={form.interestName} onChange={(e) => setForm({ ...form, interestName: e.target.value })} className="mt-1.5" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Icon</Label>
                <Select value={form.iconUrl} onValueChange={(v) => setForm({ ...form, iconUrl: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="interest-icon">
                    <SelectValue placeholder="Select an icon" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {ICON_OPTIONS.map((icon) => (
                      <SelectItem key={icon.value} value={icon.value}>
                        <div className="flex items-center gap-2">
                          <i className={`${icon.value} w-4 text-center text-sm`}></i>
                          <span>{icon.label}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Description</Label>
              <Textarea data-testid="interest-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="mt-1.5" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="interest-save">{editing ? "Save" : "Add"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Delete interest?"
        description="This will remove the interest from your portfolio."
        confirmLabel="Delete"
        onConfirm={handleDelete}
      />
    </div>
  );
}
